import axios from "axios";
import {
  VIZY_IDENTITY_BASE_URL,
  VIZY_AUTH_LOGIN,
  VIZY_AUTH_LOGOUT,
  VIZY_AUTH_TOKEN_REFRESH,
  VIZY_AUTH_REGISTER,
  VIZY_AUTH_REGISTER_VERIFICATION,
  VIZY_AUTH_REGISTER_VERIFICATION_RESEND,
  VIZY_AUTH_SEND_EMAIL_OTP,
  VIZY_AUTH_VERIFY_EMAIL_OTP,
  VIZY_AUTH_PASSWORD_RESET_REQUEST,
  VIZY_AUTH_PASSWORD_RESET_VERIFICATION,
  VIZY_AUTH_PASSWORD_RESET,
  VIZY_USER_PROFILE,
  ApplicationId,
} from "../api/url";

// ─── Storage keys ──────────────────────────────────────────────────────────────
// Identity token = the real user's token from the initial login. Used for all
// Identity/Auth APIs (userprofile, usermetadata, logout, refresh, ...).
// DMS token = the generic-user token swapped in after login. Used for all DMS
// (DealerWebService) APIs. Stored under the legacy "vizy-bility-token" key so
// existing DMS call sites keep working without modification.
const IDENTITY_TOKEN_KEY = "vizy-identity-token";
const IDENTITY_REFRESH_TOKEN_KEY = "vizy-identity-refresh-token";
const DMS_TOKEN_KEY = "vizy-bility-token";
const DMS_REFRESH_TOKEN_KEY = "vizy-refresh-token";

export const getIdentityToken = () => localStorage.getItem(IDENTITY_TOKEN_KEY);

// ─── Platform detection ────────────────────────────────────────────────────────
const getDevicePlatform = () => {
  if (typeof window === "undefined") return "Web";
  const ua = window.navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return "IOS";
  if (/Macintosh/.test(ua) && window.navigator.maxTouchPoints > 1) return "IOS";
  if (/Android/.test(ua)) return "Android";
  return "Web";
};

// ─── Dedicated axios instance for VizybilityIdentity API ──────────────────────
const vizyIdentityClient = axios.create({
  baseURL: VIZY_IDENTITY_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// Public paths — do not attach Bearer token
const PUBLIC_PATHS = [
  "/auth/login",
  "/auth/login/phone",
  "/auth/login/verification",
  "/auth/register",
  "/auth/register/verification",
  "/auth/token/refresh",
  "/auth/password/reset",
  "/auth/password/reset/request",
  "/auth/password/reset/verification",
];

// Request interceptor — attach token to protected requests
vizyIdentityClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(IDENTITY_TOKEN_KEY);
    const isPublic = PUBLIC_PATHS.some((p) => config.url?.includes(p));
    if (token && !isPublic) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor — auto-refresh on 401
let _isRefreshing = false;
let _refreshQueue = [];

const processQueue = (error, token = null) => {
  _refreshQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  _refreshQueue = [];
};

const SKIP_REFRESH_PATHS = ["/auth/login", "/auth/token/refresh"];

vizyIdentityClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isSkipped = SKIP_REFRESH_PATHS.some((p) =>
      originalRequest?.url?.includes(p),
    );

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isSkipped
    ) {
      if (!localStorage.getItem("vizy-keep-signed-in")) {
        localStorage.removeItem(IDENTITY_TOKEN_KEY);
        localStorage.removeItem(IDENTITY_REFRESH_TOKEN_KEY);
        localStorage.removeItem(DMS_TOKEN_KEY);
        localStorage.removeItem(DMS_REFRESH_TOKEN_KEY);
        if (window.location.pathname !== "/") window.location.href = "/";
        return Promise.reject(error);
      }

      if (_isRefreshing) {
        return new Promise((resolve, reject) => {
          _refreshQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = "Bearer " + token;
          return vizyIdentityClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      _isRefreshing = true;

      try {
        const currentToken = localStorage.getItem(IDENTITY_TOKEN_KEY);
        const refreshToken = localStorage.getItem(IDENTITY_REFRESH_TOKEN_KEY);
        if (!refreshToken) throw new Error("No refresh token");

        const res = await vizyIdentityClient.post(VIZY_AUTH_TOKEN_REFRESH, {
          token: currentToken,
          refreshToken,
        });
        const data = res.data?.data ?? res.data;
        const newToken = data?.token || data?.Token;
        const newRefresh = data?.refreshToken || data?.RefreshToken;

        if (!newToken) throw new Error("No token returned");

        localStorage.setItem(IDENTITY_TOKEN_KEY, newToken);
        if (newRefresh) localStorage.setItem(IDENTITY_REFRESH_TOKEN_KEY, newRefresh);

        vizyIdentityClient.defaults.headers.common.Authorization =
          "Bearer " + newToken;
        originalRequest.headers.Authorization = "Bearer " + newToken;

        processQueue(null, newToken);
        return vizyIdentityClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem(IDENTITY_TOKEN_KEY);
        localStorage.removeItem(IDENTITY_REFRESH_TOKEN_KEY);
        localStorage.removeItem(DMS_TOKEN_KEY);
        localStorage.removeItem(DMS_REFRESH_TOKEN_KEY);
        if (window.location.pathname !== "/") {
          window.location.href = "/";
        }
        return Promise.reject(refreshError);
      } finally {
        _isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

// ─── Auth service functions ────────────────────────────────────────────────────

/**
 * Login via VizybilityIdentity.
 * Returns:
 *   { token, refreshToken }         — on success
 *   { requiresMfa, userId, method } — when MFA is required
 */
export const loginService = async (email, password, rememberMe = false, { skipRoleCheck = false, appType = "EcommUser" } = {}) => {
  const response = await vizyIdentityClient.post(VIZY_AUTH_LOGIN, {
    email,
    password,
    rememberMe,
    platform: getDevicePlatform(),
    appType,
    applicationId: ApplicationId,
  });
  console.log("Login Response:", response);
  const data = response.data?.data ?? response.data;

  if (data?.requiresMfa) {
    return {
      requiresMfa: true,
      userId: data.userId,
      method: data.method || "email",
    };
  }

  if (!data?.token) throw new Error("Invalid response from auth server");

  if (!skipRoleCheck) {
    // Role restriction — only WebSiteUser may access PavingMarket
    const { jwtDecode } = await import("jwt-decode");
    const decoded = jwtDecode(data.token);
    const ROLE_CLAIM_KEYS = [
      "role",
      "roles",
      "Role",
      "Roles",
      "ApplicationRole",
      "appType",
      "AppType",
      "UserType",
      "http://schemas.microsoft.com/ws/2008/06/identity/claims/role",
      "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/role",
    ];
    const roles = ROLE_CLAIM_KEYS.reduce((all, key) => {
      const raw = decoded[key];
      if (!raw) return all;
      return all.concat(Array.isArray(raw) ? raw : [raw]);
    }, []);
    const normalize = (r) =>
      (r || "").toString().toLowerCase().replace(/\s/g, "");
    const hasAccess = roles.some((r) => normalize(r) === "ecommuser");
    if (!hasAccess) throw new Error("Unauthorized access");
  }

  return {
    token: data.token,
    refreshToken: data.refreshToken || null,
    metadata: data.metadata || null,
  };
};

/**
 * Fetch the current user's profile from VizybilityIdentity.
 * GET /api/v1/userprofile
 */
export const getUserProfile = async () => {
  const response = await vizyIdentityClient.get(VIZY_USER_PROFILE);
  return response.data?.data ?? response.data;
};

/**
 * Register a new user on VizybilityIdentity.
 * Required: email, password, confirmPassword, firstName, lastName, phoneNumber
 * Optional: countryCode, address, city, state, zipCode
 */
export const registerService = async ({
  email,
  password,
  confirmPassword,
  firstName,
  lastName,
  phoneNumber,
  countryCode,
  secondaryPhoneNumber,
  secondaryCountryCode,
  address,
  city,
  state,
  zipCode,
  country,
  receiveMarketingEmails,
  emailVerificationToken,
}) => {
  const formData = new FormData();

  // Required
  formData.append("Email", email);
  formData.append("Password", password);
  formData.append("ConfirmPassword", confirmPassword);
  formData.append("FirstName", firstName);
  formData.append("LastName", lastName);
  formData.append("PhoneNumber", phoneNumber);

  // Optional – phone
  if (countryCode) formData.append("CountryCode", countryCode);
  if (secondaryPhoneNumber)
    formData.append("SecondaryPhoneNumber", secondaryPhoneNumber);
  if (secondaryCountryCode)
    formData.append("SecondaryCountryCode", secondaryCountryCode);

  // Optional – address
  if (address) formData.append("Address", address);
  if (city) formData.append("City", city);
  if (state) formData.append("State", state);
  if (zipCode) formData.append("ZipCode", zipCode);
  if (country) formData.append("Country", country);

  // Optional – preferences
  if (receiveMarketingEmails) formData.append("ReceiveMarketingEmails", "true");

  // Required for WebSiteUser flow — token returned from verify-otp step
  if (emailVerificationToken) formData.append("EmailVerificationToken", emailVerificationToken);

  // Fixed
  formData.append("Platform", "Web");
  formData.append("AppType", "EcommUser");
  formData.append("ApplicationId", ApplicationId);

  const response = await vizyIdentityClient.post(VIZY_AUTH_REGISTER, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

/**
 * Request a password-reset email.
 * Payload: { email }
 */
export const forgotPasswordService = async (email) => {
  const response = await vizyIdentityClient.post(
    VIZY_AUTH_PASSWORD_RESET_REQUEST,
    { email, applicationId: ApplicationId, appType: "EcommUser" },
  );
  return response.data;
};

/**
 * Verify the OTP sent during password reset.
 * Payload: { email, code }
 */
export const verifyPasswordResetCodeService = async ({ email, code }) => {
  const response = await vizyIdentityClient.post(
    VIZY_AUTH_PASSWORD_RESET_VERIFICATION,
    {
      email,
      code,
      applicationId: ApplicationId,
      appType: "EcommUser"
    },
  );
  return response.data;
};

/**
 * Set a new password using the reset token from the email link.
 * Payload: { email, token, newPassword, confirmPassword }
 */
export const resetPasswordService = async ({
  email,
  token,
  newPassword,
  confirmPassword,
}) => {
  const response = await vizyIdentityClient.post(VIZY_AUTH_PASSWORD_RESET, {
    email,
    token,
    newPassword,
    confirmPassword,
    applicationId: ApplicationId,
  });
  return response.data;
};

/**
 * Send email OTP for the ecomm-user registration flow.
 * POST /api/v1/auth/ecomm-user/email/send-otp
 * Payload: { email, applicationId }
 */
export const sendRegisterVerificationService = async (email) => {
  const response = await vizyIdentityClient.post(
    VIZY_AUTH_SEND_EMAIL_OTP,
    { email, applicationId: ApplicationId },
  );
  return response.data;
};

/**
 * Resend the registration verification email.
 * POST /api/v1/auth/register/verification/resend?userId={userId}
 * Falls back to sendRegisterVerificationService(email) if userId is missing.
 */
export const resendRegisterVerificationService = async ({ userId, email }) => {
  if (!userId) {
    return sendRegisterVerificationService(email);
  }
  const response = await vizyIdentityClient.post(
    VIZY_AUTH_REGISTER_VERIFICATION_RESEND,
    null,
    { params: { userId } },
  );
  return response.data;
};

/**
 * Verify the email OTP for the ecomm-user registration flow.
 * POST /api/v1/auth/ecomm-user/email/verify-otp
 * Payload: { email, code, applicationId }
 * Returns: { verificationToken, ... }
 */
export const verifyRegisterCodeService = async ({ email, code }) => {
  const response = await vizyIdentityClient.post(
    VIZY_AUTH_VERIFY_EMAIL_OTP,
    { email, code, applicationId: ApplicationId },
  );
  return response.data;
};

/**
 * Call logout endpoint (authenticated).
 */
export const logoutService = async () => {
  try {
    await vizyIdentityClient.post(VIZY_AUTH_LOGOUT);
  } catch {
    // ignore — we clear local state regardless
  }
};

/**
 * Clear all auth tokens from localStorage.
 */
export const clearAuthStorage = () => {
  localStorage.removeItem(IDENTITY_TOKEN_KEY);
  localStorage.removeItem(IDENTITY_REFRESH_TOKEN_KEY);
  localStorage.removeItem(DMS_TOKEN_KEY);
  localStorage.removeItem(DMS_REFRESH_TOKEN_KEY);
  localStorage.removeItem("client_info");
  localStorage.removeItem("vizy-azure-token");
  localStorage.removeItem("getAzureUserData");
  localStorage.removeItem("azuretoken");
  localStorage.removeItem("OpenWarning");
  localStorage.removeItem("company_id");
  localStorage.removeItem("StoreMachineData");
};

export default vizyIdentityClient;
